<!--
// app/pages/[region]/[news]/[post].vue
-->
<script setup lang="ts">
import type { DateTimeFormatOptions } from "@intlify/core-base";
import type { CmsCategoryIds, Post } from "~/src/post/domain/models/Post";
import type { LangSlugInfo } from "~/src/post/infrastructure/CmsPostDTO";
import postUiService from "~/src/post/infrastructure/ui-services/postUiService";
import { useWebContext } from "~/app/composables/webContext";
import { isFutureDate, setAliasUrl } from "~/src/Shared/utils";
import { $LIVE_CHRONICLE_POST_CHECK } from "~/src/post/domain/service/PostService";
import GridRelatedNews from "~/app/components/pages/region-news/GridRelatedNews.vue";
import { useCompetitionStore } from "~/app/stores/competitionStore";
import { useRegions } from "~/app/composables/regions";
import { getWebContextCmsCategories } from "~/src/post/datasources/cmsCategoriesData";
import { getWebContextAllSocialProfilesSchema } from "~/src/region/datasources/regionsData";

const { getPost } = postUiService();
const { contextDomain, contextSiteTitle, webContext } = useWebContext();
const { params, fullPath } = useRoute();
const { region } = useCompetitionStore();
const { isSpain, isAmericas } = useRegions();
const regionId = region?.id;
const { locale, t } = useI18n();
const setI18nParams = useSetI18nParams();

definePageMeta({
  middleware: ["validation-news-guard", "not-found-king-of-kings-nations"],
});

const finishLiveDate = ref<string | undefined>();
const post = ref<Post>();
const slugs = ref<{ [p: string]: LangSlugInfo } | undefined>();
const isMinuteByMinute = ref<boolean>(false);
const minuteByMinuteIsLive = ref<boolean>(false);
const seoInfo = ref();

try {
  post.value = await getPost(params.post, locale.value);

  slugs.value = post.value?.slugs || {};

  const { date, dateGmt, acf, seoHeadJSON } = post.value;
  isMinuteByMinute.value = !!acf?.minuto_a_minuto;
  seoInfo.value = seoHeadJSON;

  if (isMinuteByMinute.value && !!acf?.finalizacion_del_partido) {
    const finishDate = transformFinishTimeToUTC(date, dateGmt, acf.finalizacion_del_partido);
    finishLiveDate.value = finishDate;
    if (finishDate) {
      minuteByMinuteIsLive.value = !!isFutureDate(finishDate);
    } else {
      console.error("Invalid finish date live blog posting");
    }
  } else if (isMinuteByMinute.value) {
    minuteByMinuteIsLive.value = false;
  }
} catch (e) {
  console.error(e);
  throw createError({ statusCode: 404, statusMessage: "Post not found" });
}

setI18nParams({
  en: { news: "news", post: slugs.value?.en?.slug },
  es: { news: "actualidad", post: slugs.value?.es?.slug },
  de: { news: "aktuelles", post: slugs.value?.de?.slug },
  fr: { news: "actualites", post: slugs.value?.fr?.slug },
  it: { news: "notizie", post: slugs.value?.it?.slug },
  pt: { news: "noticias", post: slugs.value["pt-br"]?.slug },
});

if (
  !post.value ||
  (post.value?.slugs && !post.value?.slugs[locale.value === "pt" ? "pt-br" : locale.value]?.slug)
) {
  throw createError({ statusCode: 404, statusMessage: "Post not found" });
}

const intervalId = ref<NodeJS.Timeout>();
const relatedCategories = ref<any[] | undefined>();

const newsCategories: CmsCategoryIds = getWebContextCmsCategories(webContext);
const regionCategoriesExist = !!newsCategories[regionId] && !!newsCategories[regionId][locale.value];
const webContextSocialProfiles = JSON.stringify(getWebContextAllSocialProfilesSchema(webContext) || []);

if (regionCategoriesExist) {
  relatedCategories.value = [
    newsCategories[regionId][locale.value].general,
    newsCategories[regionId][locale.value].featured,
  ];
}

const clearLiveChronicleInterval = () => {
  if (intervalId.value) {
    clearInterval(intervalId.value);
    intervalId.value = undefined;
  }
};

const dateOptions: DateTimeFormatOptions = {
  month: "long",
  day: "numeric",
  year: "numeric",
};

function transformFinishTimeToUTC(
  publishedDate: string,
  publishedDateGmt: string,
  finishDate: string
): string | undefined {
  const refTimeZoneDate = new Date(publishedDate).getTime();
  const refUtcDate = new Date(publishedDateGmt).getTime();
  const refDif = refTimeZoneDate - refUtcDate;
  const refDifHours = refDif / 1000 / 60 / 60;

  const gmtString =
    (refDifHours > 0 ? "+" : "") + (refDifHours >= 10 ? refDifHours : "0" + refDifHours) + ":00";
  const finishDateStandard = finishDate.replace(" ", "T");
  const dateString = finishDateStandard + gmtString;
  const _date = new Date(dateString);

  if (_date.toLocaleString() === "Invalid Date") {
    return undefined;
  }

  return new Date(dateString).toISOString();
}

const reloadEmbeds = () => {
  // reload twitter widget script
  if (window.twttr && window.twttr.widgets) {
    window.twttr.widgets.load();
  }

  // reload instagram embeds script
  if (window.instgrm && window.instgrm.Embeds) {
    window.instgrm.Embeds.process();
  }

  // reload tiktok embeds script
  if (window.tiktok) {
    window.tiktok.init();
  }
};

const fetchPost = async () => {
  try {
    post.value = await getPost(params.post, locale.value);

    const { date, dateGmt, acf } = post.value;
    isMinuteByMinute.value = !!acf?.minuto_a_minuto;

    if (isMinuteByMinute.value && !!acf?.finalizacion_del_partido) {
      const finishDate = transformFinishTimeToUTC(date, dateGmt, acf.finalizacion_del_partido);
      finishLiveDate.value = finishDate;
      if (finishDate) {
        minuteByMinuteIsLive.value = !!isFutureDate(finishDate);
      } else {
        console.error("Invalid finish date live blog posting");
      }
    } else if (isMinuteByMinute.value) {
      minuteByMinuteIsLive.value = false;
    }
  } catch (e) {
    console.error(e);
  }
};

onMounted(() => {
  reloadEmbeds();

  //minute by minute refresh
  if (minuteByMinuteIsLive.value) {
    intervalId.value = setInterval(async function () {
      await fetchPost();
      if (!minuteByMinuteIsLive.value) {
        clearLiveChronicleInterval();
      }
    }, $LIVE_CHRONICLE_POST_CHECK);
  } else if (!!intervalId.value) {
    clearLiveChronicleInterval();
  }
});

onUnmounted(() => {
  if (!!intervalId.value) {
    clearLiveChronicleInterval();
  }
});

let headline = "";
let image = "";
let datePublished = "";
let dateModified = "";
let wordCount = "";
let keywords = `"${contextSiteTitle}"`;
let articleSection: string[] | string = "";
let inLanguage = "es";
let author = `{"@type": "Organization","name": "${contextSiteTitle}","url": "https://${contextDomain}", "sameAs": ${webContextSocialProfiles}}`;

if (post.value && seoInfo.value) {
  if (seoInfo.value.schema["@graph"][0].headline) {
    headline = post.value?.seoHeadJSON.schema["@graph"][0].headline;
  }
  if (seoInfo.value?.og_image[0].url) {
    image = seoInfo.value?.og_image[0].url;
  }
  if (seoInfo.value.schema["@graph"][0].datePublished) {
    datePublished = seoInfo.value.schema["@graph"][0].datePublished;
  }
  if (seoInfo.value.schema["@graph"][0].dateModified) {
    dateModified = seoInfo.value.schema["@graph"][0].dateModified;
  }
  if (seoInfo.value.schema["@graph"][0].wordCount) {
    wordCount = seoInfo.value.schema["@graph"][0].wordCount;
  }
  if (seoInfo.value.schema["@graph"][0].keywords) {
    keywords = seoInfo.value.schema["@graph"][0].keywords.map(k => `"${k}"`);
  }
  if (seoInfo.value.schema["@graph"][0].articleSection) {
    const valuesSections = Object.values(seoInfo.value.schema["@graph"][0].articleSection);
    articleSection = valuesSections.length ? valuesSections.map(section => `"${section}"`) : "";
  }
  if (seoInfo.value.schema["@graph"][0].inLanguage) {
    inLanguage = seoInfo.value.schema["@graph"][0].inLanguage;
  }
}

if (post.value && !!post.value.author) {
  const authorImage = post.value.author.profile_image
    ? `, "image": ["${post.value.author.profile_image}"]`
    : "";
  const authorSocial = post.value.author.social_profiles.length
    ? `, "sameAs": [${post.value.author.social_profiles.map(s => {
        return `"${s.profile_url}"`;
      })}]`
    : "";
  author = `{"@type": "Person","name": "${post.value.author.complete_name}"${authorImage}${authorSocial}}`;
}

useSeoMeta({
  title: () => seoInfo.value?.title,
  ogTitle: () => seoInfo.value?.og_title,
  description: () => seoInfo.value?.description,
  ogDescription: () => seoInfo.value?.og_description,
  ogImage: () => seoInfo.value?.og_image[0].url,
  twitterCard: () => seoInfo.value?.twitter_card,
  ogType: "article",
  ogUrl: () => `https://${contextDomain}${fullPath}`,
  twitterTitle: () => seoInfo.value?.title,
  twitterDescription: () => seoInfo.value?.description,
  twitterImage: () => seoInfo.value?.og_image[0].url,
});

useHead({
  script: () => [
    {
      type: "application/ld+json",
      innerHTML: `{"@context": "https://schema.org","@type": "Article","headline": "${headline}","image": ["${image}"],"datePublished": "${datePublished}","dateModified": "${dateModified}","author": ${author},"wordCount": ${wordCount},"keywords": [${keywords}],"articleSection": [${articleSection}],"inLanguage": "${inLanguage}"}`,
    },
  ],
});
</script>

<template>
  <div v-if="post" :key="`${locale}-${post.id}`" class="mb-10 scroll-smooth">
    <div class="bg-gray-900 py-12 px-4 md:px-24">
      <div id="postHeader" class="max-w-[1200px] mx-auto">
        <p class="mb-2 text-xs font-normal text-gray-400 uppercase">
          {{ new Date(post.dateGmt).toLocaleDateString(locale, dateOptions) }}
        </p>
        <h1 class="text-2xl md:text-3xl font-bold tracking-tight text-gray-200" v-html="post.title"></h1>
        <div
          :class="['text-base md:text-lg leading-none mt-2', { 'mb-4': isMinuteByMinute }]"
          v-html="post.excerpt"
        ></div>
        <a
          v-if="isMinuteByMinute"
          href="#liveChronicle"
          class="btn btn-primary text-sm"
          v-text="
            minuteByMinuteIsLive ? $t('new.post.liveChronicle.btn') : $t('new.post.liveChronicle.btnNoLive')
          "
        ></a>
      </div>
    </div>
    <div
      v-if="post.seoHeadJSON.og_image && !!post.seoHeadJSON.og_image[0]"
      class="max-h-[570px] max-w-[1200px] mx-auto"
    >
      <NuxtImg
        :src="post.seoHeadJSON.og_image ? setAliasUrl(post.seoHeadJSON.og_image[0].url) : ''"
        :alt="post.seoHeadJSON.title"
        class="max-h-[570px] object-cover object-top w-full aspect-video"
        :width="post.seoHeadJSON.og_image[0].width"
        :height="post.seoHeadJSON.og_image[0].height"
        sizes="320px sm:810px md:1024px lg:1200px"
      />
    </div>

    <div v-if="post.author" class="mt-6 flex items-end justify-stretch gap-2 max-w-4xl mx-auto px-4">
      <div class="border border-gray-400 bg-gray-800 rounded-full w-14 h-14 overflow-hidden">
        <nuxt-img
          v-if="post.author.profile_image"
          :src="setAliasUrl(post.author.profile_image)"
          :alt="post.author.complete_name"
          width="56"
          height="56"
          fit="outside"
          class="w-14 h-14 object-cover object-top"
        />
        <div v-else class="text-gray-400 w-14 h-14 flex justify-center items-center">
          <svg
            class="w-7 h-7"
            aria-hidden="true"
            xmlns="http://www.w3.org/2000/svg"
            width="24"
            height="24"
            fill="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              fill-rule="evenodd"
              d="M14 4.182A4.136 4.136 0 0 1 16.9 3c1.087 0 2.13.425 2.899 1.182A4.01 4.01 0 0 1 21 7.037c0 1.068-.43 2.092-1.194 2.849L18.5 11.214l-5.8-5.71 1.287-1.31.012-.012Zm-2.717 2.763L6.186 12.13l2.175 2.141 5.063-5.218-2.141-2.108Zm-6.25 6.886-1.98 5.849a.992.992 0 0 0 .245 1.026 1.03 1.03 0 0 0 1.043.242L10.282 19l-5.25-5.168Zm6.954 4.01 5.096-5.186-2.218-2.183-5.063 5.218 2.185 2.15Z"
              clip-rule="evenodd"
            />
          </svg>
        </div>
      </div>
      <div class="pb-2 border-b border-gray-400 text-gray-400 flex-grow">
        <p>{{ post.author.complete_name }}</p>
      </div>
    </div>

    <div v-html="post.content.rendered" class="kql-post" />

    <div v-if="post.acf.minuto_a_minuto" class="kql-post minute mt-2 scroll-smooth" id="liveChronicle">
      <div class="bg-gray-900 rounded-lg p-2.5 md:p-4">
        <div class="flex items-center gap-2">
          <p class="kql-page-section-title !font-normal">{{ $t("new.post.liveChronicle.title") }}</p>
          <span v-if="minuteByMinuteIsLive" class="bg-red-600 w-2 h-2 mb-1 rounded-full animate-pulse"></span>
        </div>
        <hr />
        <div class="max-h-[60vh] min-h-[350px] overflow-y-scroll hide-scrollbar">
          <div v-for="(event, i) in post.acf.eventos_de_partido" :key="i">
            <div class="flex justify-start items-center mt-4 gap-2 flex-wrap px-2">
              <span class="text-primary text-xl" v-if="event.minuto_del_evento"
                >{{ event.minuto_del_evento }}'</span
              >
              <div v-if="!!event.titulo_del_evento" class="flex justify-start items-center gap-2">
                <span
                  v-if="!!event.minuto_del_evento"
                  class="w-2 h-2 rounded-full bg-primary inline-block"
                ></span>
                <p class="!text-xl">{{ event.titulo_del_evento }}</p>
              </div>
            </div>
            <p class="!mt-2 px-2" v-html="event.contenido"></p>
            <hr />
          </div>
        </div>
      </div>
    </div>

    <grid-related-news :categories="relatedCategories" :exclude="[post.id]" />
  </div>
</template>

<style scoped>
#liveChronicle {
  margin-top: -150px;
  padding-top: 150px;
}
</style>
